<template>
    <div class="secure-sign__container">
        <v-container>
            <s-s-form-header
                title="Denzapine Monitoring Service Patient Re-Challenge Request"
                document-number="PV01F40"
                version="7.0"
                effective-date="29 Feb 2024"
                review-date="29 Feb 2026"
                supersedes="6.0"
            ></s-s-form-header>
            <v-col class="text-center">
                <h1 class="mb-5">Denzapine Monitoring Service Patient Re-Challenge Request</h1>
                <h4 class="mb-8">
                    Please complete all fields before submission of this form. Missing information may lead to delays in
                    processing.
                </h4>
            </v-col>
            <v-col cols="12">
                <v-form ref="form" v-model="isFormValid" :disabled="isDisabled">
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Patient Details</h5>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::reChallengeRequestForm.patientDmsNumber"
                                :rules="validationRules.patientDmsNumber"
                                label="Patient DMS Number:"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::reChallengeRequestForm.patientFirstName"
                                :rules="validationRules.patientFirstName"
                                label="Patient First Name:"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::reChallengeRequestForm.patientLastName"
                                label="Patient Last Name:"
                                :rules="validationRules.patientLastName"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::reChallengeRequestForm.dateOfBirth"
                                label="Date of Birth:"
                                :rules="validationRules.dateOfBirth"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                        <v-col cols="12" sm="12" class="py-0">
                            <b-textarea
                                auto-grow
                                to-mutate="secure_sign_forms::reChallengeRequestForm.prohibitedFromReceivingClozapine"
                                label="The above-named patient is currently prohibited from receiving Clozapine. We wish to re-challenge this patient with Clozapine because:"
                                :rules="validationRules.prohibitedFromReceivingClozapine"
                                mandatory
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="12" class="py-0">
                            <p>
                                This justification is subject to the particular provisions in the Summary of Product
                                Characteristics allowing the supply of clozapine to certain classes of patients who
                                would otherwise be prohibited and which require the agreement of a consultant
                                haematologist. I am aware that re-challenge of patients who have history of toxic or
                                idiosyncratic granulocytopenia or agranulocytosis is contraindicated and therefore
                                restarting clozapine treatment is off-label. I attach written correspondence from a
                                consultant haematologist which supports the re-challenge of this patient and I agree to
                                the conditions of the re-challenge.
                            </p>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <h5 class="patient-form-title mt-5">Consultant Psychiatrist Signature</h5>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="8">
                            <b-input
                                to-mutate="secure_sign_forms::reChallengeRequestForm.printName"
                                :rules="validationRules.printName"
                                label="Print Name:"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <b-date-picker-store
                                hide-details
                                to-mutate="secure_sign_forms::reChallengeRequestForm.date"
                                label="Date"
                                :rules="validationRules.date"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="8">
                            <secure-sign-form-signature-component
                                ref="signaturePad"
                                :form-type="form"
                                :is-disabled="isDisabled"
                            ></secure-sign-form-signature-component>
                        </v-col>
                        <v-col
                            v-if="!isDisabled"
                            cols="12"
                            sm="4"
                            class="d-flex flex-column justify-content-end align-items-center"
                        >
                            <secure-sign-form-recaptcha
                                ref="recaptcha"
                                :valid-recaptcha="validRecaptcha"
                                :is-disabled="isDisabled"
                                @expired="expired"
                                @verify="onRecaptchaVerify"
                            ></secure-sign-form-recaptcha>

                            <v-btn
                                :loading="loading"
                                class="mt-2"
                                max-width="304px"
                                width="100%"
                                size="x-large"
                                color="primary"
                                :disabled="isDisabled"
                                @click="submit"
                                >Submit
                            </v-btn>
                        </v-col>
                    </v-row>
                    <s-s-form-footer>
                        <p>
                            Should you have any queries, please do not hesitate to contact the Denzapine Monitoring
                            Service Team on 0333 200 4141
                        </p>
                        <p>Job Code: UK-Denz-2300036 Date of prep: Feb 2024</p>
                    </s-s-form-footer>
                </v-form>
            </v-col>
        </v-container>
    </div>
</template>

<script>
import SSFormHeader from './partials/SSFormHeader.vue';
import SSFormFooter from './partials/SSFormFooter.vue';

let formControl = require('./mixins/FormControl.js').default;
export default {
    components: { SSFormFooter, SSFormHeader },
    mixins: [formControl],
    data() {
        return {
            validationRules: {
                patientDmsNumber: [(v) => !!v || t('users::validation.field.required')],
                patientFirstName: [(v) => !!v || t('users::validation.field.required')],
                patientLastName: [(v) => !!v || t('users::validation.field.required')],
                dateOfBirth: [(v) => !!v || t('users::validation.field.required')],
                prohibitedFromReceivingClozapine: [(v) => !!v || t('users::validation.field.required')],
                printName: [(v) => !!v || t('users::validation.field.required')],
                date: [(v) => !!v || t('users::validation.field.required')],
            },
            isFormValid: false,
            formType: 're-challenge-request-form',
            form: 'reChallengeRequestForm',
        };
    },
    created() {
        this.getFormData();
    },
};
</script>

<style scoped></style>
